import { DateHelperService } from '../../services/date-helper.service';

import { RouteDateUtil } from './route.date.util';
import { arrivalTimestampOf, sortsOnTime } from './route.sorts-on-time.pipe';

/**
 * Working out where each stop belongs, without touching the form.
 *
 * Split out from the component so the one piece of this feature that can move real planning data
 * is testable on its own. Everything here is pure: the caller decides when to run it and how to
 * write the result back.
 */

export interface RouteReorder {
  /** The stops in their new order. A fresh array — the caller owns how it reaches the model. */
  order: readonly unknown[];
  /** Where each stop ended up: `newIndexOfOld[oldIndex]` is its position afterwards. */
  newIndexOfOld: readonly number[];
}

const arrivalOf = (stop: unknown): unknown => (stop as Record<string, unknown> | null)?.['arrival-time'];

/** When this stop arrives, or +Infinity if it has no usable time. */
export function arrivalKeyOf(stop: unknown): number {
  return arrivalTimestampOf(arrivalOf(stop));
}

/** Whether the route's own order governs this stop, rather than the planner's hand. */
export function isScheduled(stop: unknown): boolean {
  return sortsOnTime(arrivalOf(stop));
}

/**
 * Put the scheduled stops in time order, leaving every unscheduled stop exactly where it is.
 *
 * Returns null when there is nothing to do. That answer is decided on VALUES, never on object
 * identity: writing the result back re-enters Formly, which hands out rebuilt stop objects, so an
 * identity check would never match and the sort would retrigger itself forever. "Already
 * ascending" is idempotent, which is what makes the re-entry terminate.
 *
 * A stop with no usable time keeps its index on purpose. It is a stop the planner has not
 * scheduled yet — most often one they just inserted — and its position is the only thing it has.
 */
export function reorderByArrival(stops: readonly unknown[]): RouteReorder | null {
  if (stops.length < 2) {
    return null;
  }

  const keys = stops.map(arrivalKeyOf);
  const scheduledKeys = keys.filter(k => isFinite(k));
  if (scheduledKeys.length < 2 || scheduledKeys.every((k, i) => i === 0 || scheduledKeys[i - 1] <= k)) {
    return null;
  }

  // The scheduled stops by their OLD index, ordered by time. Sorting indices rather than the
  // stops themselves keeps the permutation exact: two stops can be equal values (Formly seeds
  // `undefined` for an insert with no default), and looking their position up afterwards with
  // indexOf would report the first match for both and send the animation after the wrong row.
  // Array.prototype.sort is stable, so stops sharing a timestamp keep their relative order.
  const byTime = stops
    .map((_, index) => index)
    .filter(index => isFinite(keys[index]))
    .sort((a, b) => keys[a] - keys[b]);

  const order = [...stops];
  const newIndexOfOld = stops.map((_, index) => index);

  let next = 0;
  for (let index = 0; index < stops.length; index++) {
    if (!isFinite(keys[index])) {
      continue;
    }
    const oldIndex = byTime[next++];
    order[index] = stops[oldIndex];
    newIndexOfOld[oldIndex] = index;
  }

  return { order, newIndexOfOld };
}

/**
 * Taking a day back off a stop.
 *
 * The derived day is a one-way ratchet: `advanceToAtLeast` only ever pushes a stop forward, and
 * `deriveArrival` starts from the date part already stored on the value ("a stored date is never
 * pulled back", route.date.util.ts). So once a typed time lands a stop on the next day there is
 * nothing that brings it home — dragging it up does not, because the stored day comes with it and
 * the stop it displaces then rolls forward in its place.
 *
 * This is the way back. It takes one calendar day off the stop AND says where the stop belongs
 * afterwards, because doing only the first would be undone within the same pass: a stop that now
 * reads earlier than the one before it gets pushed straight back to the next day.
 */
export interface DayRemoval {
  /** The stop's arrival value with one calendar day taken off. */
  arrival: string;
  /** The index the stop has to move to, so the earlier time is not pushed forward again. */
  index: number;
}

/**
 * One calendar day, never 24 hours.
 *
 * Subtracting 86_400_000ms across a DST boundary moves the clock as well as the date: a stop at
 * 08:00 comes back as 07:00 or 09:00, silently changing a time the planner typed. Counting the
 * calendar keeps the clock.
 *
 * The one case it cannot keep is the spring-forward gap: on the day the clocks jump, 02:00-02:59
 * does not exist, and `setHours` resolves it to the hour after. A stop at 02:30 landing on that
 * day comes back at 03:30. There is no 02:30 to come back to, and refusing would leave the
 * planner with a cross that does nothing and no reason why — so the day comes off and the clock
 * moves to the nearest moment that exists. One day a year, and the spec below pins it so it stays
 * a decision rather than a surprise.
 */
function oneDayEarlier(arrival: string): string | null {
  const parts = DateHelperService.dateTimeToDateAndTime(arrival);
  if (!parts.date || !parts.time) {
    return null;
  }
  const parsed = DateHelperService.parseTimeString(parts.time);
  if (!parsed) {
    return null;
  }

  const shifted = DateHelperService.addDays(RouteDateUtil.localDateFromISODate(parts.date), -1);
  shifted.setHours(parsed.hours, parsed.minutes, 0, 0);
  return DateHelperService.toLocalISOString(shifted);
}

export function removeOneDay(stops: readonly unknown[], index: number, floor?: Date | null): DayRemoval | null {
  const stop = stops[index];
  const arrival = arrivalOf(stop);
  if (typeof arrival !== 'string' || !isScheduled(stop)) {
    return null;
  }

  const shifted = oneDayEarlier(arrival);
  if (!shifted) {
    return null;
  }

  // The departure is a floor the route cannot go under: no stop happens before the van leaves.
  // A first stop at 08:00 under a 10:00 departure is genuinely tomorrow, and taking a day off it
  // would be pushed straight back — a cross that does nothing except mark the entry unsaved. The
  // way to change that day is to move the departure, not the stop.
  if (floor && arrivalTimestampOf(shifted) < floor.getTime()) {
    return null;
  }

  // Where it lands: in front of the first stop it now precedes. Only earlier — a day less can
  // never move a stop further down the route, and clamping that way keeps the click predictable.
  const key = arrivalTimestampOf(shifted);
  let target = index;
  for (let i = 0; i < index; i++) {
    const other = arrivalKeyOf(stops[i]);
    if (isFinite(other) && other > key) {
      target = i;
      break;
    }
  }

  return { arrival: shifted, index: target };
}

/**
 * Where every stop ends up when one of them is moved.
 *
 * The reorder animation needs the same shape it gets from a sort — `newIndexOfOld[oldIndex]` —
 * and hand-rolling the off-by-one around the moved item is the kind of arithmetic that reads
 * fine and animates the wrong rows. Splicing an array of indices is the move itself, so it
 * cannot disagree with what actually happened to the model.
 */
export function permutationForMove(length: number, from: number, to: number): readonly number[] {
  const positions = Array.from({ length }, (_, index) => index);
  const [moved] = positions.splice(from, 1);
  positions.splice(to, 0, moved);

  const newIndexOfOld = new Array<number>(length);
  positions.forEach((oldIndex, newIndex) => {
    newIndexOfOld[oldIndex] = newIndex;
  });
  return newIndexOfOld;
}

/** One stop's arrival value, rewritten. */
export interface DayRewrite {
  index: number;
  arrival: string;
}

/**
 * Re-derive the day of every stop after `fromIndex` from the order and the clock time, instead of
 * from whatever date each one happens to have stored.
 *
 * This is the cascade behind taking a day off a stop. The stops behind it were pushed forward
 * because IT was, and the ratchet leaves them there — but "which of them only rolled because of
 * this one" cannot be answered by looking at their day offsets. A route can legitimately run
 * 08:00, 22:00, 01:00, 09:00, 08:00 across three days, where the stops after an overnight are on
 * a later day for their own reason; taking a day off all of them because the first one moved
 * would be wrong.
 *
 * So nothing is guessed. The day is derived again from the only two things time-only mode has:
 * the order of the stops and the clock time each one carries. A genuine overnight still produces
 * its day change, because 01:00 after 22:00 is still tomorrow.
 */
export function rederiveDaysAfter(stops: readonly unknown[], fromIndex: number): DayRewrite[] {
  const anchor = arrivalOf(stops[fromIndex]);
  if (typeof anchor !== 'string' || !isScheduled(stops[fromIndex])) {
    return [];
  }

  const rewrites: DayRewrite[] = [];
  let previous = new Date(anchor);

  for (let index = fromIndex + 1; index < stops.length; index++) {
    const arrival = arrivalOf(stops[index]);
    if (typeof arrival !== 'string' || !isScheduled(stops[index])) {
      // A stop the planner has not scheduled does not take part and does not move the running
      // date on, exactly as calculateRouteDates treats it.
      continue;
    }

    const parts = DateHelperService.dateTimeToDateAndTime(arrival);
    const clock = parts.time ? DateHelperService.parseTimeString(parts.time) : null;
    if (!clock) {
      continue;
    }

    // Start the candidate on the previous stop's day, then let the ordinary rule decide whether
    // this clock time means today or tomorrow.
    const candidate = new Date(previous.getFullYear(), previous.getMonth(), previous.getDate());
    candidate.setHours(clock.hours, clock.minutes, 0, 0);
    const derived = RouteDateUtil.advanceToAtLeast(candidate, previous);

    const value = DateHelperService.toLocalISOString(derived);
    if (value !== arrival) {
      rewrites.push({ index, arrival: value });
    }
    previous = derived;
  }

  return rewrites;
}
